import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysLocaleMessages,
} from '../../provider/context';
import { resolveBaseAppearance } from './theme';
import type { BaseSpinnerProps } from './types';
import { cn } from './utils';

export const BaseSpinner = forwardRef<HTMLSpanElement, BaseSpinnerProps>(
  function BaseSpinner(
    {
      decorative = false,
      label,
      appearance,
      className,
      classNames,
      style,
      ...spinnerProps
    },
    ref,
  ) {
    const messages = useMonkeysLocaleMessages();
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);

    if (typeof decorative !== 'boolean') {
      throw new TypeError('BaseSpinner decorative must be a boolean.');
    }
    if (decorative && label !== undefined) {
      throw new TypeError(
        'BaseSpinner label cannot be provided when decorative is true.',
      );
    }

    const resolvedLabel = decorative
      ? undefined
      : (label ?? messages.loading.spinner);
    if (
      !decorative &&
      (typeof resolvedLabel !== 'string' || !resolvedLabel.trim())
    ) {
      throw new TypeError('BaseSpinner label must be a non-empty string.');
    }

    return (
      <span
        {...componentAttributes}
        {...spinnerProps}
        ref={ref}
        role={decorative ? undefined : 'status'}
        aria-label={resolvedLabel}
        aria-hidden={decorative ? 'true' : undefined}
        data-monkeys-component="spinner"
        data-slot="root"
        data-decorative={decorative || undefined}
        className={cn(theme.slots.spinnerRoot, classNames?.root, className)}
        style={{
          ...theme.styles.spinner,
          ...style,
        }}
      >
        <span
          aria-hidden="true"
          data-slot="indicator"
          className={cn(theme.slots.spinnerIndicator, classNames?.indicator)}
          style={theme.styles.spinnerIndicator}
        />
      </span>
    );
  },
);
